import { lazy, ComponentType } from "react";

type ComponentModule = { default: ComponentType<any> };

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("failed to load module script")
  );
}

/**
 * Wraps React.lazy() with automatic retry + cache-busting for chunk load errors.
 * If all retries fail, forces a full page reload as last resort.
 */
export function lazyWithRetry<T extends ComponentModule>(
  importFn: () => Promise<T>,
  retries = 2
) {
  return lazy(() => {
    const attempt = (remainingRetries: number): Promise<T> =>
      importFn().catch((error: unknown) => {
        if (remainingRetries > 0 && isChunkLoadError(error)) {
          return new Promise<T>((resolve) => {
            setTimeout(() => resolve(attempt(remainingRetries - 1)), 1000);
          });
        }

        // All retries exhausted — force reload (once per session to avoid loops)
        const reloadKey = "alacart-chunk-reload";
        if (isChunkLoadError(error) && !sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, "1");
          window.location.reload();
          // Return empty component to prevent ErrorBoundary flash
          return { default: (() => null) as unknown as T["default"] } as T;
        }

        throw error;
      });

    return attempt(retries);
  });
}
